import { useEffect, useState, type FunctionComponent } from "react";
import {
  useParams,
  NavLink,
  Outlet,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Spinner from "../../../components/spinner.components";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbSeparator,
} from "../../../components/ui/breadcrumb";
import { useGetCampaignByIdAdminHttp } from "../../../hooks/http/admin/campaigns/get-campaign-by-id.admin.http";
import { useToast } from "../../../hooks/use-toast";
import { handleHttpError } from "../../../utils/utils";
import useCampaignStore from "../../../store/campaign.store";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import { Input } from "../../../components/ui/input";
import { XIcon } from "lucide-react";
import useHistoryPathStore from "../../../store/history-path.store";
import useSearchOpenStateStore from "../../../store/search-open-state.store";
import { AddPostSliderPartial } from "../../../partials/admin/menus/post/add-post-modal.admin.partials";
import { Sheet } from "../../../components/ui/sheet";
import AddVendorDraftAdminPartial from "../../../partials/admin/menus/post/add-vendor-draft.admin.partials";

export const CampaignViewAdminScreen: FunctionComponent = () => {

  const menuList = [
    {
      name: "Metrics",
      link: "campaign-metrics",
    },
    {
      name: "Live",
      link: "live-posts",
    },
    {
      name: "Posts",
      link: "post-submissions",
    },
    {
      name: "Drafts",
      link: "draft-submissions",
    },
  ];

  const {
    data: campaignData,
    setData: setCampaignData,
    setFetching: setFetchingCampaign,
  } = useCampaignStore();

  const { fetching, execute: getCampaignByIdHttp } =
    useGetCampaignByIdAdminHttp();

  const { campaignId } = useParams();
  const [searchQuery] = useSearchParams();
  const { searchOpen, setSearchOpen } = useSearchOpenStateStore();

  const [addpostOpen, setAddpostOpen] = useState(false);

  const [addVendorDraft, setAddVendorDraft] = useState(false);

  const [search, setSearch] = useState(searchQuery.get("query") || "");

  const { data, setData } = useHistoryPathStore();

  const { toast } = useToast();
  const navigate = useNavigate();

  async function getCampaignById() {
    if (!campaignId) {
      return;
    }
    setFetchingCampaign(true);
    const response = await getCampaignByIdHttp({
      body: null,
      pathParams: { id: campaignId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setCampaignData(response.data.campaign);
    setFetchingCampaign(false);
  }

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
    if (e.target.value.trim().length > 3) {
      navigate(`/admin/campaigns/${campaignId}/search?query=${e.target.value}`);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getCampaignById();
    setSearchOpen(false);
    setSearchOpen(window.location.pathname.includes("search"));
    setData(`/admin/campaigns/${campaignId}/campaign-metrics`);
  }, [campaignId, setSearchOpen]);

  return (
    <div className="flex flex-col bg-background p-8 w-full">
      <div className="flex justify-between items-center gap-4 w-full min-w-0">
        <Breadcrumb className="min-w-0 flex-1 overflow-hidden">
          <BreadcrumbList className="flex flex-nowrap items-center min-w-0">
            <BreadcrumbItem className="shrink-0">
              <Link to="/admin">
                <h1 className="font-semibold text-secondary text-xl">Home</h1>
              </Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="shrink-0 text-secondary" />
            <BreadcrumbItem className="min-w-0 max-w-lg overflow-hidden">
              <h1
                className="font-semibold text-primary text-xl truncate"
                title={campaignData?.name}
              >
                {fetching ? <Spinner width={20} /> : campaignData?.name}
              </h1>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex justify-end items-center gap-4 shrink-0">
          <div className="flex justify-between items-center border border-border rounded-lg w-full max-w-md overflow-hidden">
            {searchOpen ? (
              ""
            ) : (
              <ul className="flex justify-between items-center w-full font-semibold">
                {menuList.map((menu) => (
                  <NavLink
                    to={menu.link}
                    key={menu.link}
                    onClick={() => setSearchOpen(false)}
                    className={({ isActive }) =>
                      `px-6 h-14 flex items-center justify-center border border-transparent text-secondary rounded-lg ${isActive ? "border border-white text-white" : ""}`
                    }
                  >
                    {menu.name}
                  </NavLink>
                ))}
              </ul>
            )}

            <ul
              className={`flex items-center ${searchOpen ? "w-full" : "bg-tertiarybuttonbg rounded-lg"} h-14 `}
            >
              <div className="relative flex items-center bg-bginput px-5 py-4 rounded-10 w-full h-full">
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setSearchOpen(true);
                    setData(window.location.pathname);
                  }}
                >
                  <SearchIcon />
                </div>

                {searchOpen && (
                  <Input
                    value={search}
                    onChange={(e) => {
                      handleSearch(e);
                    }}
                    className="bg-transparent border-none w-full h-full font-semibold text-base placeholder:text-secondary"
                    placeholder="Search by name, instagram ID, and number"
                  />
                )}

                {searchOpen && (
                  <XIcon
                    onClick={() => {
                      setSearch("");
                      setSearchOpen(false);
                      navigate(data);
                      // setData([]);
                      document.getElementById("getSocials")?.click();
                    }}
                    className="right-5 absolute cursor-pointer"
                  />
                )}
              </div>
            </ul>
          </div>
          {/* <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger className="bg-tertiarybuttonbg p-4 border border-border rounded-xl cursor-pointer">
              <Plus
                className={`transition-all duration-200 ${dropdownOpen ? "rotate-45" : ""}`}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="bg-background mt-2"
              collisionPadding={34}
              align="end"
            >
              <DropdownMenuItem
                className="flex justify-end items-center p-3 w-full cursor-pointer"
                textValue="Add post"
                onClick={(e) => {
                  e.preventDefault();
                  setDropdownOpen(false); // Close dropdown first
                  setAddpostOpen(true); // Then open dialog
                }}
              >
                <div className="flex items-center gap-1">
                  <Plus className="mr-1 w-4 h-4" />
                  Add post
                </div>
              </DropdownMenuItem>
              <Separator />
              <DropdownMenuItem
                className="flex justify-end items-center p-3 w-full text-primarybuttonbg"
                textValue="Add vendor draft"
                onClick={(e) => {
                  e.preventDefault();
                  setDropdownOpen(false); // Close dropdown first"
                  setAddVendorDraft(true); // Then open sheet
                }}
              >
                <ArrowTopRightIcon />
                Add vendor draft
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu> */}

          <Sheet open={addpostOpen} onOpenChange={setAddpostOpen}>
            <AddPostSliderPartial reset={!addpostOpen} />
          </Sheet>

          <Sheet open={addVendorDraft} onOpenChange={setAddVendorDraft}>
            <AddVendorDraftAdminPartial reset={!addVendorDraft} />
          </Sheet>
        </div>
      </div>

      <Outlet />
    </div>
  );
};
